import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRegistryCount, searchRegistry } from '../api/skillScoutApi';
import { ToolCard } from '../components/ToolCard';
import type { RegistryToolResponse } from '../types/api';

export function Dashboard() {
  const [registrySize, setRegistrySize] = useState<number | null>(null);
  const [popular, setPopular] = useState<RegistryToolResponse[]>([]);
  const [trending, setTrending] = useState<RegistryToolResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const [searchResults, countResult] = await Promise.allSettled([
          searchRegistry('', [], 50),
          getRegistryCount(),
        ]);

        if (countResult.status === 'fulfilled') {
          setRegistrySize(countResult.value.total);
        }

        if (searchResults.status === 'fulfilled') {
          const tools = searchResults.value;
          if (countResult.status !== 'fulfilled') setRegistrySize(tools.length);

          // Popular: top 5 by weekly_downloads
          const byDownloads = [...tools].sort((a, b) => b.weekly_downloads - a.weekly_downloads);
          setPopular(byDownloads.slice(0, 5));

          // Trending: top 3 by most recently published
          const byDate = [...tools].sort((a, b) => {
            const aDate = a.last_published ? new Date(a.last_published).getTime() : 0;
            const bDate = b.last_published ? new Date(b.last_published).getTime() : 0;
            return bDate - aDate;
          });
          setTrending(byDate.slice(0, 3));
        }
      } catch (e) {
        setError(String(e));
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  return (
    <div className="min-h-screen bg-background text-white">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white">Skill Scout</h1>
          <p className="text-gray-400 text-sm mt-1">
            Tool registry browser &amp; recommendation dashboard
          </p>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div className="rounded-xl bg-surface p-4">
            <div className="text-2xl font-bold text-secondary">
              {loading ? '…' : (registrySize ?? '—')}
            </div>
            <div className="text-xs text-gray-400 mt-1">Registry tools</div>
          </div>
          <div className="rounded-xl bg-surface p-4">
            <div className="text-2xl font-bold text-secondary">
              {loading ? '…' : popular.length}
            </div>
            <div className="text-xs text-gray-400 mt-1">Popular tools</div>
          </div>
          <div className="rounded-xl bg-surface p-4 col-span-2 sm:col-span-1">
            <div className="text-2xl font-bold text-secondary">
              {loading ? '…' : trending.length}
            </div>
            <div className="text-xs text-gray-400 mt-1">Trending this week</div>
          </div>
        </div>

        {error && (
          <div className="rounded-xl bg-red-900/40 border border-red-700 p-4 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Popular Tools */}
        <section>
          <h2 className="text-lg font-semibold mb-3">Popular Tools</h2>
          {loading ? (
            <p className="text-gray-400 text-sm">Loading…</p>
          ) : popular.length === 0 ? (
            <p className="text-gray-400 text-sm">No tools available.</p>
          ) : (
            <div className="space-y-3">
              {popular.map((tool) => (
                <ToolCard key={tool.slug} tool={tool} />
              ))}
            </div>
          )}
        </section>

        {/* Trending strip */}
        <section>
          <h2 className="text-lg font-semibold mb-3">Trending This Week</h2>
          {loading ? (
            <p className="text-gray-400 text-sm">Loading…</p>
          ) : trending.length === 0 ? (
            <p className="text-gray-400 text-sm">No trending tools yet.</p>
          ) : (
            <div className="space-y-3">
              {trending.map((tool) => (
                <ToolCard key={tool.slug} tool={tool} />
              ))}
            </div>
          )}
        </section>

        {/* Quick links */}
        <nav className="flex gap-4 flex-wrap">
          <Link
            to="/registry"
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            Browse Registry →
          </Link>
          <Link
            to="/digest"
            className="rounded-lg bg-surface px-4 py-2 text-sm font-medium text-white hover:bg-card transition-colors"
          >
            Digest History →
          </Link>
        </nav>
      </div>
    </div>
  );
}
